import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchAffiliateApplications, decideAffiliateApplication } from "../../../api/affiliates/affiliateAPI";
import { AFFILIATE_STATUS } from "../../../utils/constants/affiliateConstants";
import ConfirmationModal from "../../../components/ui/popup/ConfirmationModal";
import SmallAlert from "../../../components/ui/alerts/SmallAlert";

const AffiliateApplicationsSection = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, userId: null, decision: null, userName: "" });
  const [processing, setProcessing] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: "", message: "" });
  const [statusFilter, setStatusFilter] = useState("all");

  const STATUS_LABELS = {
    [AFFILIATE_STATUS.REV]: "Pending Review",
    [AFFILIATE_STATUS.REJ]: "Rejected",
    [AFFILIATE_STATUS.CCP]: "Pending Connected Account",
    [AFFILIATE_STATUS.ACT]: "Active",
  };

  const loadApplications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchAffiliateApplications();

      if (result.status === 401 || result.status === 403) {
        navigate("/custom-admin", { replace: true });
        return;
      }

      if (result.success) {
        setApplications(result.data.results || []);
      } else {
        setError("Failed to load affiliate applications");
      }
    } catch (e) {
      console.error("Error loading affiliate applications:", e);
      setError("Failed to load affiliate applications");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const showConfirmation = (userId, decision, userName) => {
    setConfirmModal({ isOpen: true, userId, decision, userName });
  };

  const handleConfirm = async () => {
    const { userId, decision } = confirmModal;
    setProcessing(true);
    setConfirmModal({ isOpen: false, userId: null, decision: null, userName: "" });

    try {
      const result = await decideAffiliateApplication(userId, decision);

      if (result.success) {
        setAlert({
          show: true,
          type: "success",
          message: result.data.message || `Application ${decision}ed successfully!`
        });
        // Reload applications list
        loadApplications();
      } else {
        setAlert({
          show: true,
          type: "error",
          message: result.message || `Failed to ${decision} application`
        });
      }
    } catch (e) {
      console.error(`Error ${decision}ing application:`, e);
      setAlert({
        show: true,
        type: "error",
        message: `Failed to ${decision} application`
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = () => {
    setConfirmModal({ isOpen: false, userId: null, decision: null, userName: "" });
  };

  const handleAccept = (userId, userName) => {
    showConfirmation(userId, "accept", userName);
  };

  const handleReject = (userId, userName) => {
    showConfirmation(userId, "reject", userName);
  };

  if (loading) {
    return <div>Loading affiliate applications...</div>;
  }

  if (error) {
    return <div style={{ color: "red" }}>{error}</div>;
  }

  return (
    <>
      {alert.show && (
        <SmallAlert
          alert="True"
          type={alert.type}
          message={alert.message}
          dismissOnClickOutside={true}
          onDismiss={() => setAlert({ show: false, type: "", message: "" })}
          style={{ marginBottom: "20px" }}
        />
      )}

      <div>
        {/* Status Filter Tabs */}
        <div style={{ display: "flex", gap: "8px", marginBottom: "20px", borderBottom: "2px solid #e0e0e0", paddingBottom: "8px" }}>
          <button
            onClick={() => setStatusFilter("all")}
            style={{
              padding: "8px 16px",
              background: statusFilter === "all" ? "#004789" : "transparent",
              color: statusFilter === "all" ? "white" : "#004789",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: statusFilter === "all" ? "600" : "400",
            }}
          >
            All ({applications.length})
          </button>
          <button
            onClick={() => setStatusFilter(AFFILIATE_STATUS.REV)}
            style={{
              padding: "8px 16px",
              background: statusFilter === AFFILIATE_STATUS.REV ? "#004789" : "transparent",
              color: statusFilter === AFFILIATE_STATUS.REV ? "white" : "#004789",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: statusFilter === AFFILIATE_STATUS.REV ? "600" : "400",
            }}
          >
            Pending Review ({applications.filter(a => a.status === AFFILIATE_STATUS.REV).length})
          </button>
          <button
            onClick={() => setStatusFilter(AFFILIATE_STATUS.REJ)}
            style={{
              padding: "8px 16px",
              background: statusFilter === AFFILIATE_STATUS.REJ ? "#004789" : "transparent",
              color: statusFilter === AFFILIATE_STATUS.REJ ? "white" : "#004789",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: statusFilter === AFFILIATE_STATUS.REJ ? "600" : "400",
            }}
          >
            Rejected ({applications.filter(a => a.status === AFFILIATE_STATUS.REJ).length})
          </button>
          <button
            onClick={() => setStatusFilter(AFFILIATE_STATUS.CCP)}
            style={{
              padding: "8px 16px",
              background: statusFilter === AFFILIATE_STATUS.CCP ? "#004789" : "transparent",
              color: statusFilter === AFFILIATE_STATUS.CCP ? "white" : "#004789",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: statusFilter === AFFILIATE_STATUS.CCP ? "600" : "400",
            }}
          >
            Pending Payout ({applications.filter(a => a.status === AFFILIATE_STATUS.CCP).length})
          </button>
          <button
            onClick={() => setStatusFilter(AFFILIATE_STATUS.ACT)}
            style={{
              padding: "8px 16px",
              background: statusFilter === AFFILIATE_STATUS.ACT ? "#004789" : "transparent",
              color: statusFilter === AFFILIATE_STATUS.ACT ? "white" : "#004789",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: statusFilter === AFFILIATE_STATUS.ACT ? "600" : "400",
            }}
          >
            Active ({applications.filter(a => a.status === AFFILIATE_STATUS.ACT).length})
          </button>
        </div>

      <h3>Affiliate Applications</h3>
      {(() => {
        const filteredApps = statusFilter === "all" ? applications : applications.filter(a => a.status === statusFilter);
        return filteredApps.length === 0 ? (
          <p>No affiliate applications found.</p>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "20px" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #ddd" }}>
                <th style={{ padding: "12px", textAlign: "left" }}>Name</th>
                <th style={{ padding: "12px", textAlign: "left" }}>Email</th>
                <th style={{ padding: "12px", textAlign: "left" }}>Status</th>
                <th style={{ padding: "12px", textAlign: "left" }}>Social Medias</th>
                <th style={{ padding: "12px", textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredApps.map((app) => (
              <tr key={app.user_id} style={{ borderBottom: "1px solid #eee" }}>
                <td style={{ padding: "12px" }}>
                  {app.first_name} {app.last_name}
                </td>
                <td style={{ padding: "12px" }}>{app.email}</td>
                <td style={{ padding: "12px" }}>
                  <span style={{
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    fontWeight: "500",
                    backgroundColor: 
                      app.status === AFFILIATE_STATUS.REV ? "#FFF4E6" :
                      app.status === AFFILIATE_STATUS.REJ ? "#FFE6E6" :
                      app.status === AFFILIATE_STATUS.CCP ? "#E6F3FF" :
                      app.status === AFFILIATE_STATUS.ACT ? "#E6FFE6" : "#f0f0f0",
                    color:
                      app.status === AFFILIATE_STATUS.REV ? "#CC7A00" :
                      app.status === AFFILIATE_STATUS.REJ ? "#CC0000" :
                      app.status === AFFILIATE_STATUS.CCP ? "#0066CC" :
                      app.status === AFFILIATE_STATUS.ACT ? "#00CC00" : "#666",
                  }}>
                    {STATUS_LABELS[app.status] || app.status}
                  </span>
                </td>
                <td style={{ padding: "12px" }}>
                  {app.social_medias && app.social_medias.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                      {app.social_medias.map((sm, idx) => (
                        <div key={idx} style={{ fontSize: "13px" }}>
                          <div>
                            <strong>{sm.source}:</strong> {sm.handler}
                            {sm.followers && ` (${sm.followers.toLocaleString()} followers)`}
                          </div>
                          {sm.description && (
                            <div style={{ color: "#666", fontSize: "12px", marginTop: "4px", fontStyle: "italic" }}>
                              {sm.description}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    "No social media"
                  )}
                </td>
                <td style={{ padding: "12px", textAlign: "center" }}>
                  <button
                    className="cta-button primary short"
                    onClick={() => handleAccept(app.user_id, `${app.first_name} ${app.last_name}`)}
                    style={{ marginRight: "8px" }}
                    disabled={processing || app.status !== "REV"}
                  >
                    Accept
                  </button>
                  <button
                    className="cta-button secondary short"
                    onClick={() => handleReject(app.user_id, `${app.first_name} ${app.last_name}`)}
                    disabled={processing || app.status !== "REV"}
                  >
                    Reject
                  </button>
                </td>
              </tr>
              ))}
            </tbody>
          </table>
        );
      })()}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        title={`${confirmModal.decision === "accept" ? "Accept" : "Reject"} Affiliate Application`}
        message={`Are you sure you want to ${confirmModal.decision} the affiliate application for ${confirmModal.userName}?`}
        confirmText={confirmModal.decision === "accept" ? "Accept" : "Reject"}
        cancelText="Cancel"
        confirmButtonClass="primary"
        cancelButtonClass="secondary"
      />
    </div>
    </>
  );
};

export default AffiliateApplicationsSection;
